import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { HelpCircle } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Label } from "@/components/ui/Input";
import { CardTitleWithHelp, HoverHintTrigger, PageTitleWithHelp } from "@/components/ui/title-help";
import { formatNumber, MRP_PLANNING_TLA_BOM_VARIABLE_NAME } from "@/lib/mrp";

const TLA_COL_CLASS =
  "sticky left-0 z-10 w-[1%] whitespace-nowrap bg-white px-3 py-2 after:absolute after:inset-y-0 after:right-0 after:w-px after:bg-slate-200";

const FORWARD_MONTH_OPTIONS = [3, 4, 5, 6, 9, 12] as const;

const BOM_MESSAGE_PREVIEW = 260;

const PAGE_HOVER_HINT =
  "Open Megaventory sales order lines with remaining unbatched quantity (line quantity minus public.batch_item allocations), on headers that are not cancelled, pending, or closed. Bucketed by sales_order.sgs_ship_by_date calendar month starting with the current UTC month. TLA (FI-*-TLA-* assembly) comes from the BoM topLevel slot via process_boms at depth 1 — the same shallow explosion as MRP Planning. Distinct finished-good SKUs are exploded once per batch with per-SKU fallback when the batch fails; resolver errors skip only those SKUs and those lines aggregate under Unknown.";

const MONTHLY_REPORT_HOVER_HINT =
  "Rows are TLAs (BoM topLevel component_sku). Columns are calendar months by ship-by date. Total sums unfulfilled quantity across the window; Average is total divided by the number of months shown.";

const SUMMARY_COL_CLASS =
  "whitespace-nowrap border-l border-slate-200 bg-slate-50/80 px-4 py-2 text-right tabular-nums font-medium text-slate-900";

const FILTER_SELECT_CLASS =
  "h-10 w-full rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500";

const HELP_BUTTON_CLASS =
  "inline-flex shrink-0 rounded p-0.5 text-slate-400 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-1";

interface ForwardDemandBomResolutionError {
  sku: string;
  message: string;
}

interface ForwardDemandMonthlyLineRow {
  month_first_day: string;
  month_label: string;
  bom_component_sku: string | null;
  assembly_code: string | null;
  assembly_display_name: string | null;
  quantity_sum: number;
  line_count: number;
}

interface ForwardDemandOrderLinesByMonthResponse {
  variable_name: string;
  from_inclusive: string;
  to_exclusive: string;
  forward_months: number;
  bom_resolution_errors: ForwardDemandBomResolutionError[];
  unknown_order_skus: string[];
  rows: ForwardDemandMonthlyLineRow[];
}

type ForwardDemandPivotRow = {
  key: string;
  label: string;
  sublabel: string | null;
  isUnknown: boolean;
  quantitiesByMonth: Map<string, number>;
};

function buildForwardDemandPivot(rows: ForwardDemandMonthlyLineRow[]): {
  monthLabels: string[];
  pivotRows: ForwardDemandPivotRow[];
} {
  const monthOrder: { label: string; firstDay: string }[] = [];
  const monthSeen = new Set<string>();
  for (const row of rows) {
    if (!monthSeen.has(row.month_label)) {
      monthSeen.add(row.month_label);
      monthOrder.push({ label: row.month_label, firstDay: row.month_first_day });
    }
  }
  monthOrder.sort((left, right) => left.firstDay.localeCompare(right.firstDay));

  const rowMap = new Map<string, ForwardDemandPivotRow>();
  for (const row of rows) {
    const isUnknown = row.bom_component_sku == null && row.assembly_code == null;
    const key = isUnknown
      ? "__unknown__"
      : (row.bom_component_sku ?? row.assembly_code ?? "__unknown__").trim();
    const label = isUnknown
      ? "Unknown"
      : row.assembly_code != null && row.assembly_code.trim() !== ""
        ? row.assembly_code.trim()
        : (row.bom_component_sku ?? "Unknown").trim();

    let pivotRow = rowMap.get(key);
    if (!pivotRow) {
      const sublabel =
        !isUnknown &&
        row.bom_component_sku != null &&
        row.assembly_code != null &&
        row.assembly_code.trim() !== "" &&
        row.bom_component_sku.trim() !== row.assembly_code.trim()
          ? row.bom_component_sku.trim()
          : row.assembly_display_name != null && String(row.assembly_display_name).trim() !== "" &&
              String(row.assembly_display_name).trim() !== label
            ? String(row.assembly_display_name).trim()
            : null;

      pivotRow = {
        key,
        label,
        sublabel,
        isUnknown,
        quantitiesByMonth: new Map(),
      };
      rowMap.set(key, pivotRow);
    }

    const existing = pivotRow.quantitiesByMonth.get(row.month_label) ?? 0;
    pivotRow.quantitiesByMonth.set(row.month_label, existing + row.quantity_sum);
  }

  const pivotRows = [...rowMap.values()].sort((left, right) => {
    if (left.isUnknown && !right.isUnknown) return 1;
    if (!left.isUnknown && right.isUnknown) return -1;
    return left.label.localeCompare(right.label, undefined, { sensitivity: "base", numeric: true });
  });

  return { monthLabels: monthOrder.map((month) => month.label), pivotRows };
}

function pivotRowTotal(pivotRow: ForwardDemandPivotRow, monthLabels: string[]): number {
  let sum = 0;
  for (const monthLabel of monthLabels) {
    sum += pivotRow.quantitiesByMonth.get(monthLabel) ?? 0;
  }
  return sum;
}

function pivotRowAverage(total: number, monthCount: number): number {
  if (monthCount <= 0) return 0;
  return total / monthCount;
}

function truncateBomMessage(raw: string): string {
  const trimmed = raw.trim();
  if (trimmed.length <= BOM_MESSAGE_PREVIEW) return trimmed;
  return `${trimmed.slice(0, BOM_MESSAGE_PREVIEW)}…`;
}

function formatMonthlyRangeSummary(data: ForwardDemandOrderLinesByMonthResponse): string {
  return [
    `Slot: ${data.variable_name}`,
    `Range: ${data.from_inclusive} → exclusive ${data.to_exclusive}`,
    `Forward months: ${data.forward_months}`,
  ].join(" · ");
}

function UnknownOrderSkusHoverPanel({
  unknownOrderSkus,
  bomResolutionErrors,
}: {
  unknownOrderSkus: string[];
  bomResolutionErrors: ForwardDemandBomResolutionError[];
}): JSX.Element {
  if (unknownOrderSkus.length === 0) {
    return (
      <p className="max-w-xs text-slate-700">
        No sales order line SKUs in this window rolled up to Unknown. If you still see Unknown quantities, refresh the
        page after the API reloads.
      </p>
    );
  }

  return (
    <div className="max-w-sm space-y-2">
      <p className="font-medium text-slate-800">
        Sales order SKUs with no resolved {MRP_PLANNING_TLA_BOM_VARIABLE_NAME} TLA ({unknownOrderSkus.length})
      </p>
      <ul className="max-h-64 list-none space-y-1 overflow-y-auto p-0 m-0 font-mono text-[11px] leading-snug text-slate-800">
        {unknownOrderSkus.map((sku) => (
          <li key={sku}>{sku}</li>
        ))}
      </ul>
      {bomResolutionErrors.length > 0 ? (
        <div className="space-y-1 border-t border-slate-200 pt-2 text-xs text-slate-700">
          <p className="font-medium text-slate-800">BoM resolver errors</p>
          <ul className="list-none space-y-1 p-0 m-0">
            {bomResolutionErrors.map((bomErr) => (
              <li key={bomErr.sku}>
                <span className="font-mono">{bomErr.sku}</span>
                <span> — {truncateBomMessage(bomErr.message)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}

export function ForwardDemandPage() {
  const [forwardMonths, setForwardMonths] = useState<number>(6);

  const monthlyQuery = useQuery({
    queryKey: ["manufacturing", "forward-demand", "order-lines-by-month", { forward_months: forwardMonths }],
    queryFn: () =>
      apiFetch<ForwardDemandOrderLinesByMonthResponse>(
        "/api/manufacturing/forward-demand/order-lines-by-month",
        {
          query: { forward_months: forwardMonths },
        },
      ),
    staleTime: 2 * 60_000,
  });

  const bomResolutionErrors = monthlyQuery.data?.bom_resolution_errors ?? [];
  const pivot = useMemo(
    () => buildForwardDemandPivot(monthlyQuery.data?.rows ?? []),
    [monthlyQuery.data?.rows],
  );
  const tlaRowCount = pivot.pivotRows.length;
  const hasPivotData = (monthlyQuery.data?.rows.length ?? 0) > 0;
  const pivotColumnCount = pivot.monthLabels.length + 3;
  const unknownOrderSkus = monthlyQuery.data?.unknown_order_skus ?? [];

  return (
    <div className="space-y-4">
      <header>
        <PageTitleWithHelp hoverHint={PAGE_HOVER_HINT}>Forward demand</PageTitleWithHelp>
      </header>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Filters</CardTitle>
        </CardHeader>
        <CardBody className="grid gap-3 pt-0 md:grid-cols-2">
          <div>
            <Label htmlFor="forward-months">Forward window</Label>
            <select
              id="forward-months"
              aria-label="Number of calendar months starting with the current UTC month"
              className={`mt-1 ${FILTER_SELECT_CLASS}`}
              value={forwardMonths}
              onChange={(event) => {
                setForwardMonths(Number.parseInt(event.target.value, 10));
              }}
            >
              {FORWARD_MONTH_OPTIONS.map((monthCount) => (
                <option key={monthCount} value={monthCount}>
                  {monthCount} months (including current month)
                </option>
              ))}
            </select>
          </div>
          {monthlyQuery.data ? (
            <p className="text-xs text-slate-500 md:col-span-2">{formatMonthlyRangeSummary(monthlyQuery.data)}</p>
          ) : null}
        </CardBody>
      </Card>

      {bomResolutionErrors.length > 0 ? (
        <div
          role="alert"
          className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950 shadow-sm ring-1 ring-amber-200/70"
        >
          <p className="font-medium">
            The BoM resolver skipped {bomResolutionErrors.length} finished-good SKU
            {bomResolutionErrors.length === 1 ? "" : "s"} during process_boms (those order lines aggregate under Unknown
            below).
          </p>
          <ul className="mt-2 list-disc space-y-1.5 pl-5 marker:text-amber-800">
            {bomResolutionErrors.map((bomErr) => (
              <li key={bomErr.sku}>
                <span className="font-mono text-slate-900">{bomErr.sku}</span>
                <span className="text-slate-700"> — {truncateBomMessage(bomErr.message)}</span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      <Card>
        <CardHeader className="pb-2">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <CardTitleWithHelp hoverHint={MONTHLY_REPORT_HOVER_HINT}>
              Unfulfilled lines by month (BoM slot {MRP_PLANNING_TLA_BOM_VARIABLE_NAME})
            </CardTitleWithHelp>
            {monthlyQuery.data && !monthlyQuery.isLoading && !monthlyQuery.error ? (
              <p className="text-xs text-slate-500">
                {tlaRowCount} {tlaRowCount === 1 ? "TLA" : "TLAs"} × {pivot.monthLabels.length}{" "}
                {pivot.monthLabels.length === 1 ? "month" : "months"}
              </p>
            ) : null}
          </div>
        </CardHeader>
        <CardBody className="pt-0">
          <div className="overflow-auto rounded-lg border border-slate-200">
            <table className="min-w-full border-collapse text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                  <th className={`${TLA_COL_CLASS} bg-slate-50`}>TLA (BoM component)</th>
                  {pivot.monthLabels.map((monthLabel) => (
                    <th key={monthLabel} className="whitespace-nowrap px-4 py-2 text-right">
                      {monthLabel}
                    </th>
                  ))}
                  <th className={`${SUMMARY_COL_CLASS} bg-slate-50`}>Total</th>
                  <th className="whitespace-nowrap px-4 py-2 text-right">Average</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {monthlyQuery.isLoading ? (
                  <tr>
                    <td colSpan={Math.max(3, pivotColumnCount)} className="px-4 py-8 text-center text-slate-500">
                      Loading…
                    </td>
                  </tr>
                ) : monthlyQuery.error ? (
                  <tr>
                    <td colSpan={Math.max(3, pivotColumnCount)} className="px-4 py-6 text-red-700">
                      {(monthlyQuery.error as Error).message ??
                        String((monthlyQuery.error as { message?: unknown })?.message)}
                    </td>
                  </tr>
                ) : !hasPivotData ? (
                  <tr>
                    <td colSpan={Math.max(3, pivotColumnCount)} className="px-4 py-8 text-center text-slate-500">
                      No unfulfilled sales lines for this span.
                    </td>
                  </tr>
                ) : (
                  pivot.pivotRows.map((pivotRow) => {
                    const total = pivotRowTotal(pivotRow, pivot.monthLabels);
                    const average = pivotRowAverage(total, pivot.monthLabels.length);
                    return (
                      <tr key={pivotRow.key} className="group hover:bg-slate-50/70">
                        <td className={`${TLA_COL_CLASS} relative group-hover:bg-slate-50/70`}>
                          {pivotRow.isUnknown ? (
                            <div className="flex items-center gap-1.5">
                              <span className="italic text-slate-500">Unknown</span>
                              <HoverHintTrigger
                                trigger={
                                  <button
                                    type="button"
                                    className={HELP_BUTTON_CLASS}
                                    aria-label={`Sales order SKUs with no resolved ${MRP_PLANNING_TLA_BOM_VARIABLE_NAME} TLA`}
                                  >
                                    <HelpCircle className="h-4 w-4" />
                                  </button>
                                }
                              >
                                <UnknownOrderSkusHoverPanel
                                  unknownOrderSkus={unknownOrderSkus}
                                  bomResolutionErrors={bomResolutionErrors}
                                />
                              </HoverHintTrigger>
                            </div>
                          ) : (
                            <div className="space-y-0.5">
                              <span className="font-mono text-brand-800">{pivotRow.label}</span>
                              {pivotRow.sublabel != null ? (
                                <div className="text-xs text-slate-500">{pivotRow.sublabel}</div>
                              ) : null}
                            </div>
                          )}
                        </td>
                        {pivot.monthLabels.map((monthLabel) => {
                          const quantity = pivotRow.quantitiesByMonth.get(monthLabel);
                          return (
                            <td
                              key={monthLabel}
                              className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-slate-800"
                            >
                              {quantity != null && quantity !== 0 ? formatNumber(quantity) : "—"}
                            </td>
                          );
                        })}
                        <td className={SUMMARY_COL_CLASS}>{formatNumber(total)}</td>
                        <td className="whitespace-nowrap px-4 py-2 text-right tabular-nums text-slate-800">
                          {formatNumber(average)}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
}
